import { Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { ACCOUNT_USERS_SEED } from '@common/data';
import type { AccountUser, AccountUserPage, AccountUserQuery } from '@common/types';

const LIST_LATENCY_MS = 5000;
const DETAIL_LATENCY_MS = 5000;

@Injectable({ providedIn: 'root' })
export class AccountUsersService {
  private readonly users = signal<AccountUser[]>(ACCOUNT_USERS_SEED.slice());
  private readonly _revision = signal<number>(0);

  readonly all = this.users.asReadonly();
  readonly revision = this._revision.asReadonly();

  list(query: AccountUserQuery): Observable<AccountUserPage> {
    let result: AccountUser[] = this.users().slice();

    const term = query.search.trim().toLowerCase();
    if (term.length > 0) {
      result = result.filter(
        (g) =>
          g.name.toLowerCase().includes(term) ||
          g.surname.toLowerCase().includes(term) ||
          g.email.toLowerCase().includes(term),
      );
    }

    const statusFilters = query.filters['status'];
    if (statusFilters?.length) {
      const allowed = new Set(statusFilters);
      result = result.filter((g) => allowed.has(g.status));
    }

    if (query.sortField) {
      const field = query.sortField as keyof AccountUser;
      const direction = query.sortOrder === 'desc' ? -1 : 1;
      result.sort((a, b) => {
        const av = a[field] ?? '';
        const bv = b[field] ?? '';
        if (av < bv) return -1 * direction;
        if (av > bv) return 1 * direction;
        return 0;
      });
    }

    const total = result.length;
    const start = Math.max(0, query.first);
    const end = start + Math.max(1, query.rows);
    const data = result.slice(start, end);

    return of({ data, total }).pipe(delay(LIST_LATENCY_MS));
  }

  getById(id: string): Observable<AccountUser | undefined> {
    return of(this.users().find((g) => g.id === id)).pipe(delay(DETAIL_LATENCY_MS));
  }
}
